import React from 'react';
import { Link } from 'react-router-dom';
import {
  UtensilsCrossed, Home as HomeIcon, Grid3x3,
  PlusSquare, Paintbrush, Building, Building2,
  Briefcase, ArrowRight, Phone, Flame, Wrench, Ruler,
} from 'lucide-react';

const BANNER_IMG = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1400&q=85";

const services = [
  {
    icon: Building2,
    title: "Custom Home Build",
    desc: "We bring your vision to life with fully custom home builds — designed, engineered, and built in-house from the ground up. From architectural planning and permit acquisition to energy-efficient construction and final finishes, we manage every step. Your custom home will balance function, beauty, and long-term performance.",
    highlights: ["Full architectural design", "Permit acquisition included", "Energy-efficient construction", "End-to-end management"],
    img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80",
  },
  {
    icon: Briefcase,
    title: "Commercial Finishing",
    desc: "We specialize in transforming commercial interiors with custom millwork, structural modifications, and mechanical, electrical, and plumbing (MEP) integration tailored to your business needs. Our team delivers high-performance finishes that align with zoning laws, building occupancy codes, and workflow optimization.",
    highlights: ["Custom millwork & finishes", "MEP integration", "Zoning & code compliance", "Workflow optimization"],
    img: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=600&q=80",
  },
  {
    icon: Building,
    title: "Condominium Finishing",
    desc: "We specialize in condo finishing that meets all building codes, fire separation requirements, and condominium board bylaws. Our team handles everything from space planning and MEP coordination to permit acquisition and board approval packages. We work efficiently to minimize disruption to your neighbours.",
    highlights: ["Building code compliant", "Board approval packages", "MEP coordination", "Minimal disruption"],
    img: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80",
  },
  {
    icon: Flame,
    title: "Plaster Moulding & Fireplace Mantle",
    desc: "Elevate your interiors with custom decorative plasterwork, ornate crown mouldings, coffered ceilings, and bespoke fireplace mantles. Our skilled craftsmen blend traditional techniques with modern design to create timeless architectural details that define the character of any space.",
    highlights: ["Crown & decorative mouldings", "Custom fireplace mantles", "Coffered ceilings", "Heritage & modern styles"],
    img: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&q=80",
  },
  {
    icon: HomeIcon,
    title: "Residential Renovation",
    desc: "We upgrade homes from single-room refreshes to complete whole-home transformations. From open-concept kitchens and spa-style bathrooms to living rooms, staircases, bedrooms, and basements, we redesign every corner. Our team handles structural changes and modern layout transformations, all managed by a single in-house crew.",
    highlights: ["Complete home transformation", "Structural modifications", "Load-bearing wall removal", "Single crew management"],
    img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80",
  },
  {
    icon: PlusSquare,
    title: "Home Additions",
    desc: "Need more room to grow? We build home additions that add space and long-term value. Our team designs and constructs second-storey additions, main-floor bump-outs, and garage conversions that integrate flawlessly with your home's structure and style. Every project is fully permitted, code-compliant, and engineered for durability.",
    highlights: ["Second-storey additions", "Main-floor bump-outs", "Garage conversions", "Fully permitted & engineered"],
    img: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&q=80",
  },
  {
    icon: UtensilsCrossed,
    title: "Kitchen Cabinets & Refinishing",
    desc: "Whether you're upgrading to custom cabinetry or refreshing existing cabinets with a refinish, we deliver precision craftsmanship and beautiful results. We handle design, fabrication, installation, hardware selection, and finishing — transforming your kitchen storage into a standout feature of your home.",
    highlights: ["Custom cabinet fabrication", "Cabinet refacing & refinishing", "Hardware & accessory selection", "Precision installation"],
    img: "https://images.unsplash.com/photo-1639751787355-bbc3ed1fd639?w=600&q=80",
  },
  {
    icon: Paintbrush,
    title: "Painting & Decoration",
    desc: "We elevate your space with professional-grade interior and exterior painting, feature walls, trim detailing, and high-end decorative finishes. Our team uses low-VOC, durable paints that are safe for families and built to last in Canadian climates. With our in-house expertise, you'll get cohesive colours and textures that match your home's style.",
    highlights: ["Interior & exterior painting", "Feature walls & trim detailing", "Low-VOC durable paints", "Professional colour guidance"],
    img: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=600&q=80",
  },
  {
    icon: Grid3x3,
    title: "Flooring & Tiling",
    desc: "We install hardwood, vinyl plank, laminate, ceramic, and natural stone flooring with expert care. Whether it's a full-home update or one room at a time, our contractors handle subfloor leveling, underlayment, moisture protection, and layout to ensure beautiful results with a long-lasting finish.",
    highlights: ["Hardwood, vinyl, laminate", "Ceramic & natural stone", "Subfloor leveling", "Moisture protection"],
    img: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=600&q=80",
  },
  {
    icon: Wrench,
    title: "Restoration",
    desc: "We bring damaged, deteriorated, or heritage properties back to life. From water damage and fire restoration to historical facade repair and structural reinforcement, our team combines traditional craftsmanship with modern building science to restore your property to its original character — or better.",
    highlights: ["Water & fire damage repair", "Heritage property restoration", "Structural reinforcement", "Facade & masonry repair"],
    img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80",
  },
  {
    icon: Ruler,
    title: "Millwork",
    desc: "Custom millwork adds character, function, and lasting value to any space. We design and build custom built-ins, bookcases, trim packages, wainscoting, panelling, shelving systems, and architectural woodwork — all crafted in-house and installed with precision. From contemporary minimalist to classic traditional, we match your vision.",
    highlights: ["Custom built-ins & bookcases", "Wainscoting & panelling", "Trim & casing packages", "In-house fabrication"],
    img: "https://images.unsplash.com/photo-1540518614846-7eded433c457?w=600&q=80",
  },
];

export default function Services() {
  return (
    <main data-testid="services-page">

      {/* BANNER */}
      <section
        className="relative h-72 md:h-96 pt-20 flex items-end"
        style={{ backgroundImage: `url(${BANNER_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2E8] mb-2">WHAT WE OFFER</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white leading-none">
            Our Services
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-4">
                Your trusted Building partner in Ontario
              </h2>
              <p className="text-base text-slate-600 leading-relaxed">
                From concept to completion, Ariana General Contracting provides complete build-outs for residential and commercial spaces. We handle everything from demolition to final finishes. Through our professional team of designers and builders we are committed to deliver quality workmanship, transparent communication, and reliable execution on every project.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/contact" data-testid="services-get-quote-btn" className="bg-[#00A2E8] hover:bg-[#0088C4] text-white px-8 py-4 font-bold text-sm tracking-widest transition-colors duration-200 text-center">
                GET A FREE QUOTE
              </Link>
              <a href="tel:+14165703834" className="border-2 border-slate-300 text-slate-700 hover:border-[#00A2E8] hover:text-[#00A2E8] px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 flex items-center gap-2 justify-center">
                <Phone className="w-4 h-4" /> CALL US
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col gap-12">
            {services.map((service, i) => {
              const Icon = service.icon;
              const isEven = i % 2 === 0;
              return (
                <div
                  key={i}
                  data-testid={`service-${i}`}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white border border-slate-200 overflow-hidden hover:shadow-[0_10px_40px_-10px_rgba(0,162,232,0.15)] transition-all duration-300"
                >
                  {/* Image */}
                  <div className={`h-64 lg:h-auto overflow-hidden ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <img
                      src={service.img}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  {/* Content */}
                  <div className={`p-8 lg:p-10 flex flex-col justify-center ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#00A2E8]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#00A2E8]" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-slate-900">{service.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">{service.desc}</p>
                    <div className="grid grid-cols-2 gap-2 mb-6">
                      {service.highlights.map((h, j) => (
                        <div key={j} className="flex items-start gap-2 text-xs text-slate-600">
                          <div className="w-1 h-1 rounded-full bg-[#00A2E8] mt-1.5 shrink-0" />
                          {h}
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/contact"
                      data-testid={`service-quote-btn-${i}`}
                      className="self-start flex items-center gap-2 font-bold text-sm text-[#00A2E8] hover:gap-3 transition-all duration-200 tracking-wide group"
                    >
                      GET A QUOTE <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 bg-[#00A2E8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-4">
            Don't See What You Need?
          </h2>
          <p className="text-white/80 text-base max-w-xl mx-auto mb-8">
            We offer a wide range of additional services. Contact us and let us know what you have in mind — we're happy to help.
          </p>
          <Link to="/contact" data-testid="services-bottom-cta-btn" className="inline-block bg-white text-[#00A2E8] hover:bg-slate-100 px-8 py-4 font-black text-sm tracking-widest transition-colors duration-200">
            CONTACT US TODAY
          </Link>
        </div>
      </section>

    </main>
  );
}
